import { useTranslation } from 'react-i18next';

import { FeedbackCollapseContext } from './feedback-card';

import type { GradingMode } from '@agavi/blocks';
import type React from 'react';

import { AddRowButton } from '@/components/ui/add-row-button';
import { useCapability } from '@/hooks/use-capabilities';

export interface TargetedSection {
  /** React key for this section */
  key: string;
  /** Section heading; omit to render without one (used when a block has only one section active) */
  heading?: string;
  /** Cards + any block-specific wrapper (e.g. DnD context) for this section */
  content: React.ReactNode;
  /** Whether to render an Add button below this section (omit entirely for fixed, non-addable sections like Sig Fig) */
  canAddMore?: boolean;
  addLabel?: string;
  onAdd?: () => void;
  /**
   * Why nothing can be added, shown in place of the Add button.
   *
   * OPTIONAL, and the default is the point: a section that cannot be added to says "No Targeted
   * Feedback options available" unless the block has something more useful to say. Six of the
   * eleven blocks passed nothing here and rendered a blank space where the explanation belongs —
   * the sentence existed, four blocks repeated it, and the rest silently went without. Making the
   * shell own it means a block has to opt OUT of explaining itself rather than remember to opt in.
   *
   * Override where the block genuinely knows more: Numerical's "Set a Correct Value and Unit"
   * tells a teacher what to DO, which the generic sentence cannot.
   */
  addDisabledReason?: string;
  /**
   * Cards that stay BELOW the Add button.
   *
   * For a section that ends in a catch-all: Numerical's units section is a list of specific units
   * followed by "Any Other Unit", and the Add button acts on the list, not on the catch-all. Put
   * the general case under the button and the section reads in the order it is evaluated —
   * specific first, everything-else last.
   */
  footer?: React.ReactNode;
  /**
   * Whether this section currently has NO cards.
   *
   * The shell needs both facts to say the right thing. "Nothing can be added" and "there is
   * nothing here" are different states: a section whose every option already has feedback cannot
   * be added to either, and announcing "No Targeted Feedback options available" above a list of
   * them would be a plain contradiction. So the sentence appears only when the section is both
   * closed to additions AND empty.
   */
  isEmpty?: boolean;
}

export interface FeedbackPanelProps {
  gradingMode: GradingMode;
  /** Text shown when gradingMode is 'optional', REPLACING the whole panel (a plain note, no
   * cards at all) — legacy behavior for blocks that haven't migrated to the unified positive-slot
   * model yet. Omit entirely (or don't pass it) once a block's `correctSlot` renders an
   * appropriate "Any Response" card for optional mode itself — the panel then renders normally
   * for every mode, no special-cased early return. */
  optionalNote?: string;
  /**
   * The locked "No Sensor Data" card, ABOVE No Answer, because it genuinely outranks it: both fire
   * on an untouched block and the hardware one wins. A bare card like `noAnswerSlot`, not a headed
   * group — its own locked label already says what it is, and a heading repeating that is noise.
   */
  noSensorDataSlot?: React.ReactNode;
  /** Contents of the locked "No Answer" section at the top */
  noAnswerSlot: React.ReactNode;
  /** Contents of the locked "Partial Answer" section, only for blocks whose answer format supports it */
  partialAnswerSlot?: React.ReactNode;
  /**
   * The reading-quality cards, BELOW No Answer, because that is where they resolve.
   *
   * They were briefly grouped with No Sensor Data above No Answer, which read better and was not
   * true: every evaluator gates them after the blank check. The panel's order is a claim about
   * resolution order, and the two sensor kinds genuinely sit on either side of No Answer — no data
   * beats a blank, a poor reading only matters once there IS one. Their positions teach that.
   *
   * The heading is OPTIONAL and callers omit it for a lone card, whose own locked label already
   * names the threshold it fires on. A heading over one self-describing row is a second name for
   * the same thing. It earns its place from two cards up, where it says what they have in common
   * and matches the Flow rung that catches them both.
   */
  poorSensorSection?: { heading?: string; content: React.ReactNode };
  /** Section heading for the Correct area — omit for blocks where the locked Check+green
   * card already reads unambiguously as "Correct" without a redundant text label above it. */
  correctHeading?: string;
  /** FeedbackCard(s) for the locked correct state */
  correctSlot: React.ReactNode;
  /** Independently-gated targeted sections, evaluated/rendered in order (graded mode only) */
  targetedSections?: TargetedSection[];
  /** Section heading for the Default Incorrect area (graded mode only) */
  incorrectHeading: string;
  /** FeedbackCard for the locked incorrect state */
  incorrectSlot: React.ReactNode;
}

export function FeedbackPanel({
  gradingMode, optionalNote, noSensorDataSlot, poorSensorSection, noAnswerSlot, partialAnswerSlot,
  correctHeading, correctSlot, targetedSections, incorrectHeading, incorrectSlot,
}: FeedbackPanelProps) {
  const { t } = useTranslation();
  /**
   * Targeted Feedback, placed by mode: AFTER the positive card in graded, BEFORE it otherwise.
   *
   * The SAME sections either way — each block hands them over as data and this is the only
   * thing that renders them, so there is no second version to drift. Only the position moves,
   * and it moves because the card below it means two different things. In graded the positive
   * card is `correct`, a terminal outcome checked FIRST, so targeted states sit under it. In
   * survey and optional there is no correct, and `any_answer` / `any_response` is the
   * CATCH-ALL — the analogue of Default Incorrect, which is also last.
   *
   * Specific before general, in every mode.
   */
  /*
   * OPTIONAL has no targeted section at all — not an empty one with its Add button still on
   * screen. The mode is inert: one catch-all, nothing to split the answer space with, and a
   * button offering to add what the mode cannot fire is the editor contradicting the evaluator.
   * Hidden here rather than in eleven blocks, because it is one decision — see `targetedFiresIn`.
   */
  /*
   * SURVEY TARGETING FOLLOWS THE FLOW CAPABILITY, which is the one gate in the app that crosses
   * features — so it is deliberate rather than drift.
   *
   * Targeting every answer in a survey starves `any_answer`, the catch-all that All Correct tests
   * for, and `DEFAULT_FLOW_CONFIG` advances a task on All Correct alone. The student cannot leave.
   * A teacher with Flow can see that and route around it; a teacher without Flow has no panel to
   * do it in, so the same authoring move becomes a dead end with no instrument to see it by.
   *
   * Flow off is the extreme beginner, and a survey with one catch-all is the right survey for
   * them. Gating the ADD only, as everywhere else: states already authored keep their cards and
   * stay deletable, which is both the way out and the fix for a task that is already stuck.
   *
   * The ENGINE is untouched — `targetedFiresIn` is shared with the student runtime and knows
   * nothing about capabilities, so an authored targeted state goes on firing exactly as before.
   */
  const mayTargetInSurvey = useCapability('flow');
  const surveyGated = (section: TargetedSection) =>
    gradingMode === 'survey' && !mayTargetInSurvey && !section.footer
      ? { hide: section.isEmpty === true, add: false }
      : { hide: false, add: true };

  const targetedBlock = gradingMode === 'optional' ? undefined : targetedSections?.filter(
    (section) => !surveyGated(section).hide,
  ).map((section) => (
    /* `gap-3` rather than margins on the heading and the button, plus `:empty` hiding.
     *
     * With margins, an EMPTY section measured double: a block element with no height normally
     * lets the margins above and below collapse through it, but every block hands its list
     * over as a `flex flex-col` container, and a flex container establishes a formatting
     * context that stops collapsing. So heading-12 + nothing + 12-button read as 24px with no
     * cards and 12px with one — the section grew a gap by having less in it.
     *
     * `:empty` matches an element with no child nodes at all, which is exactly the state that
     * list is in when there are no cards (dnd-kit's DndContext and SortableContext are context
     * providers and render no DOM of their own, so the list div is this section's direct
     * child). Hiding it removes it as a flex item, and one gap remains either way. */
    <div key={section.key} className='flex flex-col gap-3 px-6 pb-3 [&>*:empty]:hidden'>
      {section.heading && (
        <p className='text-xs font-semibold uppercase tracking-wider text-ws-muted'>{section.heading}</p>
      )}
      {section.content}
      {/* A button when there is something to press, a sentence when there is not.
       *
       * It used to be one disabled button either way, which kept the dashed outline and the
       * button shape for what is only an explanation — "No Targeted Feedback options
       * available" looked pressable and was not. A disabled control says "not yet"; this says
       * "not applicable", and the two should not look alike. */}
      {section.canAddMore && section.addLabel && section.onAdd && surveyGated(section).add ? (
        <AddRowButton
          label={section.addLabel}
          onClick={section.onAdd}
        />
      ) : section.isEmpty && !section.footer ? (
        // Suppressed when a footer is present: a section ending in a catch-all is never empty,
        // whatever the list above the button holds, so "No Targeted Feedback options
        // available" would be describing the list while contradicting the card beneath it.
        <p className='py-3 text-center text-sm italic text-ws-text-disabled'>
          {section.addDisabledReason ?? t('blocks.triggers.noTargetedFeedbackOptionsAvailable')}
        </p>
      ) : null}
      {section.footer}
    </div>
  ));

  if (gradingMode === 'optional' && optionalNote) {
    return (
      <div className='animate-[panelEnter_0.18s_ease_forwards] flex flex-1 flex-col items-center justify-center gap-4 p-8'>
        <p className='text-center text-sm text-ws-muted'>{optionalNote}</p>
      </div>
    );
  }

  return (
    // No overflow/flex sizing here — this is nested inside InputBlockFrame's own
    // `flex-1 overflow-y-auto` div, which is not a flex container, so a second
    // `overflow-y-auto` here would just create a non-scrolling box (never actually
    // overflows, since nothing bounds its height) that would intercept the sticky
    // positioning of anything above it instead of the ancestor that actually scrolls.
    <FeedbackCollapseContext.Provider value={gradingMode}>
    <div className='animate-[panelEnter_0.18s_ease_forwards] flex flex-col'>
      {/* No Answer / Partial Answer — graded and survey only. Optional mode has no unanswered
          state of its own (see positiveTriggerForMode) — leaving a block blank is just as
          accepted an outcome as answering it, so there's nothing to distinguish here. */}
      {/* No Sensor Data, No Answer, then the quality cards — the order every evaluator gates them
          in. Hidden entirely in optional mode: a block nobody is grading has nothing to route. */}
      {gradingMode !== 'optional' && (
        <div className='flex flex-col gap-3 px-6 pt-3 pb-3'>
          {noSensorDataSlot}
          {noAnswerSlot}
          {partialAnswerSlot}
        </div>
      )}

      {gradingMode !== 'optional' && poorSensorSection && (
        <div className='flex flex-col gap-3 px-6 pb-3'>
          {poorSensorSection.heading && (
            <p className='text-xs font-semibold uppercase tracking-wider text-ws-muted'>{poorSensorSection.heading}</p>
          )}
          {poorSensorSection.content}
        </div>
      )}

      {gradingMode !== 'graded' && targetedBlock}

      {/* Correct/Any Answer/Any Response — always visible, one card whose meaning is entirely
          determined by the current mode (see positiveTriggerForMode). Optional mode skips the
          No Answer block above, so this needs its own pt-3 to land at the same distance from
          the top of the panel as No Answer does in the other two modes. */}
      {/* `pt-3` exists because optional skips the No Answer block above and would otherwise sit
          tight against the top — so it applies only when nothing else precedes this card. With
          targeted sections above it, they supply the spacing and a second helping double-counts. */}
      <div className={`px-6 ${gradingMode === 'optional' && !targetedBlock?.length ? 'pt-3 ' : ''}${gradingMode === 'graded' ? 'pb-3' : 'pb-6'}`}>
        {correctHeading && (
          <p className='mb-3 text-xs font-semibold uppercase tracking-wider text-ws-muted'>{correctHeading}</p>
        )}
        {correctSlot}
      </div>

      {gradingMode === 'graded' && targetedBlock}

      {/* Default Incorrect — only in graded mode */}
      {gradingMode === 'graded' && (
        <div className='flex flex-col gap-3 px-6 pb-6'>
          <p className='text-xs font-semibold uppercase tracking-wider text-ws-muted'>{incorrectHeading}</p>
          {incorrectSlot}
        </div>
      )}
    </div>
    </FeedbackCollapseContext.Provider>
  );
}
